<script lang="ts">
	//Stores
	import { devices_settings, devices_status } from 'src/stores/device-runtime/status';
	//Components
	import NightSchedule from '../NightModeComponents/NightSchedule.svelte';
	import NightModeListV2 from '../NightModeComponents/NightModeListV2.svelte';
	import NightModeDeviceOffAlert from '../NightModeComponents/NightModeDeviceOffAlert.svelte';
	//Services
	import { format } from 'svelte-i18n';
	import { gen2Request } from 'src/services/gen_2_http_reqs';
	//Helpers
	import {
		get_gen_2_component_channel_structure,
		get_gen_2_component_from_category,
		get_mode,
		get_device_component_index,
	} from 'src/helpers/interface_helpers';
	import { onSettingsResponse } from 'src/helpers/device_settings_helpers';
	import { supportsColorModeChange } from 'src/helpers/night_mode_helpers';
	//Types
	import type { SettingRequest } from 'src/types/requests/settings/setting';
	//Design system components
	import { Toggle } from '@Shelly/shelly-design-system';
	// Types
	import type { InputClickEvent } from 'src/types/InputClickEvent';
	import { devices_config } from '@devices/index';

	export let device;

	const status = devices_status.getByRealId(device.id);
	const settings = devices_settings.getByRealId(device.id);
	const method = `${get_gen_2_component_from_category(device)}.SetConfig`;
	const mode = get_mode(device, status, settings);

	const componentKey = get_gen_2_component_channel_structure(device);
	const componentSettings = settings?.[componentKey];
	if (!componentSettings || !componentSettings.night_mode) {
		console.error(`Night mode settings not found for ${componentKey} on device ${device.id}`);
	}

	let nightMode = componentSettings?.night_mode || {
		enable: false,
		active_between: ['00:00', '00:00'],
		brightness: 50,
	};

	let hasColorControl = nightMode.hasOwnProperty('rgb') && nightMode.rgb !== undefined;
	let hasWhiteSlider = nightMode.hasOwnProperty('white') && nightMode.white !== undefined;
	let hasCT = nightMode.hasOwnProperty('ct') && nightMode.ct !== undefined;

	let valueRange = { minValue: 3000, maxValue: 6500 };
	$: {
		let configRange = devices_config[device.type]?.modes[mode]?.color_temp || {
			minValue: 3000,
			maxValue: 6500,
		};

		const currentSettings = devices_settings.getByRealId(device.id);
		const currentComponentKey = get_gen_2_component_channel_structure(device);
		if (currentSettings && currentComponentKey) {
			const settingsCTRange = currentSettings[currentComponentKey]?.ct_range;
			if (settingsCTRange && Array.isArray(settingsCTRange) && settingsCTRange.length === 2) {
				valueRange = {
					minValue: settingsCTRange[0],
					maxValue: settingsCTRange[1],
				};
			} else {
				valueRange = configRange;
			}
		} else {
			valueRange = configRange;
		}
	}

	let checked = nightMode.enable;

	let showModal = false;

	if (nightMode.white > 100) nightMode.white = 100;

	if (
		(Array.isArray(nightMode['active_between']) && nightMode['active_between'].length < 1) ||
		!Array.isArray(nightMode['active_between'])
	) {
		nightMode['active_between'] = ['00:00', '00:00'];
	}

	$: props = {
		start: nightMode['active_between'][0],
		end: nightMode['active_between'][1],
		brightness: nightMode.brightness,
		mode: nightMode.mode !== undefined ? nightMode.mode : null,
		rgb: hasColorControl ? nightMode.rgb : undefined,
		white: hasWhiteSlider ? nightMode.white : undefined,
		ct: hasCT ? nightMode.ct : undefined,
		valueRange,
		min: mode === 'rgb' ? 2 : 1,
	};

	const setNightMode = async () => {
		const params = {
			id: get_device_component_index(device),
			config: {
				night_mode: {
					enable: checked,
					active_between: nightMode.active_between,
					brightness: nightMode.brightness != undefined ? +nightMode.brightness : null,
					mode: nightMode.mode !== undefined ? nightMode.mode : null,
				},
			},
		};
		if (hasColorControl) {
			params.config.night_mode['rgb'] = nightMode.rgb;
		}
		if (hasCT) {
			params.config.night_mode['ct'] = nightMode.ct;
		}

		if (hasWhiteSlider) {
			params.config.night_mode['white'] = +nightMode.white;
		}
		const res: SettingRequest = await gen2Request(device, method, params);

		if (res.isok) {
			nightMode.enable = checked;
			settings[get_gen_2_component_channel_structure(device)].night_mode = nightMode;
			devices_settings.update_devices_settings(device.id, settings);
		}
		onSettingsResponse(res, device);
	};

	const handleToggler = async (e: InputClickEvent) => {
		checked = e.target.checked;
		setNightMode();
	};

	const handleSave = (e) => {
		const _nightMode = { ...e.detail };
		nightMode = {
			...nightMode,
			..._nightMode,
			active_between: [_nightMode.start, _nightMode.end],
		};
		Object.keys(nightMode).forEach((key) => {
			if (nightMode[key] === undefined) {
				delete nightMode[key];
			}
		});
		setNightMode();
	};
</script>

<Toggle size="l" {checked} on:click={handleToggler} id="toggle-mybk1nu">
	{$format('label_enable_night_mode')}
</Toggle>
{#if supportsColorModeChange(device.type)}
	<NightModeDeviceOffAlert enabled={checked} />
{/if}
<hr class="mb-m mt-s" />

{#key `${checked}-${nightMode.active_between?.[0]}-${nightMode.active_between?.[1]}`}
	<NightModeListV2 {nightMode} disabled={!checked} on:edit={() => (showModal = true)} />
{/key}

{#if showModal}
	<NightSchedule {props} {device} deviceType={device.type} bind:showModal on:save={handleSave} />
{/if}
